import { useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { DuplicateCandidate } from "../../../../shared/contracts.js";
import { Button } from "../../../ui/Button.js";
import { Dialog, DialogActions } from "../../../ui/Dialog.js";
import { reasonError } from "./OrderForm.js";

/**
 * The possible-duplicate warning (spec §14, §17 "Possible duplicates require
 * a warning and an audited override reason").
 *
 * The server refused the command with 409 `DUPLICATE_CASE` and named the
 * matching cases; this dialog links to each one so Parts can look before
 * deciding, and lets them continue only with a 5–500 character reason. The
 * caller resubmits the *unchanged* input with that reason — nothing typed
 * in the form is touched — and the server records the reason with the
 * matched case ids in the activity history.
 *
 * The wholesale forms use the same dialog for a 409
 * `DUPLICATE_WHOLESALE_ORDER` (wholesale spec §3.2): they pass the matching
 * orders as ready-made `links` — a WS number, where it opens, and why it
 * matched — instead of case candidates.
 */

/** One matching order as the dialog lists it: its number, where it opens, and why it matched. */
export type DuplicateLink = Readonly<{ key: string; number: string; path: string; reason: string }>;

export interface DuplicateWarningDialogProps {
  open: boolean;
  /** The matching cases a 409 `DUPLICATE_CASE` named; the default when `links` is absent. */
  candidates?: readonly DuplicateCandidate[];
  /** Ready-made links (the wholesale side); take precedence over `candidates`. */
  links?: readonly DuplicateLink[];
  /** Back to the form, nothing sent. */
  onClose: () => void;
  /** Resubmit with this reason. */
  onProceed: (reason: string) => void;
  /** The resubmission is in flight. */
  pending?: boolean;
  /** What the resubmission failed with, shown in the dialog. */
  error?: string | null;
  /** "Create anyway" for a new order, "Save anyway" for an edit. */
  proceedLabel?: string;
}

/** Where a matching case opens: the All orders list with the case selected. */
export function candidatePath(candidate: DuplicateCandidate): string {
  return `/parts?view=all&case=${encodeURIComponent(candidate.caseId)}`;
}

export function candidateReasonLabel(candidate: DuplicateCandidate): string {
  if (candidate.reason === "same_ro") return "Same RO number";
  return candidate.partNumber
    ? `Same part ${candidate.partNumber} on this vehicle`
    : "Same part on this vehicle";
}

/** The case candidates as links into the Parts workspace. */
export function caseDuplicateLinks(candidates: readonly DuplicateCandidate[]): DuplicateLink[] {
  return candidates.map((candidate) => ({
    key: `${candidate.caseId}:${candidate.reason}:${candidate.partNumber ?? ""}`,
    number: candidate.caseNumber,
    path: candidatePath(candidate),
    reason: candidateReasonLabel(candidate),
  }));
}

export function DuplicateWarningDialog({
  open,
  candidates = [],
  links,
  onClose,
  onProceed,
  pending = false,
  error = null,
  proceedLabel = "Create anyway",
}: DuplicateWarningDialogProps) {
  if (!open) return null;
  return (
    <DuplicateWarningContent
      links={links ?? caseDuplicateLinks(candidates)}
      onClose={onClose}
      onProceed={onProceed}
      pending={pending}
      error={error}
      proceedLabel={proceedLabel}
    />
  );
}

function DuplicateWarningContent({
  links,
  onClose,
  onProceed,
  pending,
  error,
  proceedLabel,
}: Required<Omit<DuplicateWarningDialogProps, "open" | "candidates">>) {
  const [reason, setReason] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const reasonId = useId();
  const hintId = useId();
  const errorId = useId();
  const shownError = localError ?? error;

  const proceed = () => {
    const problem = reasonError(reason);
    if (problem) {
      setLocalError(problem);
      reasonRef.current?.focus();
      return;
    }
    setLocalError(null);
    onProceed(reason.trim());
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Possible duplicate order"
      description="Another open order already matches this one. Review it before continuing — the reason you give is kept in the activity history."
      pending={pending}
      initialFocusRef={reasonRef}
    >
      <ul className="duplicate-list">
        {links.map((link) => (
          <li key={link.key} className="duplicate-list__item">
            <Link to={link.path} className="duplicate-list__link">
              {link.number}
            </Link>
            <span className="duplicate-list__reason">{link.reason}</span>
          </li>
        ))}
        {links.length === 0 ? (
          <li className="duplicate-list__item duplicate-list__item--unknown">
            The matching order could not be identified.
          </li>
        ) : null}
      </ul>

      <div className="field" data-invalid={shownError ? "true" : undefined}>
        <div className="field__labels">
          <label htmlFor={reasonId} className="field__label">
            Override reason
          </label>
        </div>
        <textarea
          ref={reasonRef}
          id={reasonId}
          className="text-input textarea-input"
          data-size="desktop"
          rows={3}
          value={reason}
          disabled={pending}
          aria-required="true"
          aria-invalid={shownError ? true : undefined}
          aria-describedby={[hintId, shownError ? errorId : null].filter(Boolean).join(" ")}
          onChange={(event) => {
            setReason(event.target.value);
            if (localError) setLocalError(null);
          }}
        />
        <p id={hintId} className="field__hint">
          Why this order is not the same one. Stored with your name in the activity history.
        </p>
        {shownError ? (
          <p id={errorId} role="alert" className="field__error">
            {shownError}
          </p>
        ) : null}
      </div>

      <DialogActions>
        <Button variant="secondary" onClick={onClose} disabled={pending}>
          Go back
        </Button>
        <Button variant="primary" onClick={proceed} busy={pending}>
          {proceedLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
